import React, { useEffect, useState, useCallback } from 'react';
import { financeAPI, exportAPI, downloadBlob } from '../api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { Wallet, Plus, Trash2, Download, Pencil } from 'lucide-react';

const money = (v) => (v == null ? '\u2014' : `\u20b9${Number(v).toLocaleString('en-IN')}`);
const RANGES = [['month', 'This Month'], ['quarter', 'This Quarter'], ['year', 'This Year'], ['custom', 'Custom']];
const emptyExpense = { expense_date: new Date().toISOString().slice(0, 10), category: '', amount: '', vendor: '', payment_method: 'bank', reference: '', description: '', notes: '' };

export const FinancePage = () => {
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState('overview');
  const [range, setRange] = useState('year');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [prof, setProf] = useState(null);
  const [agg, setAgg] = useState(null);
  const [expenses, setExpenses] = useState({ expenses: [], total: 0 });
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyExpense);
  const [editId, setEditId] = useState(null);
  const [newCat, setNewCat] = useState('');

  const canAdd = hasPermission('finance.expense.add');
  const canEdit = hasPermission('finance.expense.edit');
  const canDelete = hasPermission('finance.expense.delete');
  const canProfit = hasPermission('finance.profitability.view');

  const params = useCallback(() => {
    const p = {};
    if (range === 'custom') { if (customFrom) p.date_from = customFrom; if (customTo) p.date_to = customTo; }
    else p.period = range;
    return p;
  }, [range, customFrom, customTo]);

  const load = useCallback(async () => {
    try {
      const [e, c] = await Promise.all([financeAPI.expenses(params()), financeAPI.categories()]);
      setExpenses(e.data); setCategories(c.data);
      if (canProfit) { const pr = await financeAPI.profitability(params()); setProf(pr.data); }
      try { const ag = await financeAPI.aggregate(); setAgg(ag.data); } catch { /* payment.view not held */ }
    } catch (err) { toast.error('Failed to load finance data'); }
  }, [params, canProfit]);

  useEffect(() => { load(); }, [load]);

  const saveExpense = async () => {
    if (!form.category || !form.amount) { toast.error('Category and amount are required'); return; }
    const payload = { ...form, amount: Number(form.amount), expense_date: new Date(form.expense_date).toISOString() };
    try {
      if (editId) await financeAPI.updateExpense(editId, payload);
      else await financeAPI.createExpense(payload);
      toast.success(editId ? 'Expense updated' : 'Expense recorded');
      setShowForm(false); setForm(emptyExpense); setEditId(null); load();
    } catch (e) { toast.error('Failed to save expense'); }
  };

  const editExpense = (ex) => {
    setForm({ expense_date: (ex.expense_date || '').slice(0, 10), category: ex.category, amount: ex.amount, vendor: ex.vendor || '', payment_method: ex.payment_method || 'bank', reference: ex.reference || '', description: ex.description || '', notes: ex.notes || '' });
    setEditId(ex.id); setShowForm(true);
  };

  const delExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await financeAPI.deleteExpense(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try { await financeAPI.createCategory(newCat.trim()); setNewCat(''); toast.success('Category added'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const doExport = async (fmt) => {
    try { const res = await exportAPI.download('profitability', { ...params(), fmt }); downloadBlob(res, `finance.${fmt}`); toast.success('Export ready'); }
    catch (e) { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-outfit font-semibold flex items-center gap-2"><Wallet className="h-6 w-6 text-primary" /> Finance & Profitability</h1>
          <p className="text-sm text-muted-foreground">Company expenses, revenue and net profit.</p>
        </div>
        <div className="flex gap-2">
          {canProfit && hasPermission('report.export') && (
            <>
              <button onClick={() => doExport('xlsx')} data-testid="finance-export-xlsx" className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-primary/10 text-primary rounded-lg"><Download className="h-4 w-4" /> Excel</button>
              <button onClick={() => doExport('csv')} data-testid="finance-export-csv" className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 rounded-lg">CSV</button>
            </>
          )}
          {canAdd && <button onClick={() => { setForm(emptyExpense); setEditId(null); setShowForm(true); }} data-testid="finance-add-expense" className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-primary text-white rounded-lg"><Plus className="h-4 w-4" /> Add Expense</button>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map(([k, label]) => (
          <button key={k} onClick={() => setRange(k)} data-testid={`finance-range-${k}`} className={`px-3 py-1.5 rounded-full text-sm ${range === k ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>{label}</button>
        ))}
        {range === 'custom' && (
          <span className="flex items-center gap-2">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} data-testid="finance-custom-from" className="px-2 py-1 border rounded-lg text-sm bg-white dark:bg-slate-900" />
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} data-testid="finance-custom-to" className="px-2 py-1 border rounded-lg text-sm bg-white dark:bg-slate-900" />
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <button onClick={() => setTab('overview')} className={`px-3 py-1.5 rounded-lg text-sm ${tab === 'overview' ? 'bg-primary/10 text-primary' : ''}`} data-testid="finance-tab-overview">Overview</button>
          <button onClick={() => setTab('expenses')} className={`px-3 py-1.5 rounded-lg text-sm ${tab === 'expenses' ? 'bg-primary/10 text-primary' : ''}`} data-testid="finance-tab-expenses">Expenses</button>
        </div>
      </div>

      {agg && (
        <div className="clay-card p-4" data-testid="finance-company-ledger">
          <h3 className="font-medium mb-1">Company Contract Ledger (all applicable projects)</h3>
          <p className="text-xs text-muted-foreground mb-3">Derived from project payment ledgers. Scope: every project-like lead — a Project Serial, an assigned Architect, or won/converted ({agg.project_count} projects), whatever its current stage.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><div className="text-xs text-muted-foreground">Total Approved Contract Value</div><div className="text-xl font-semibold" data-testid="agg-contract">{money(agg.total_approved_contract_value)}</div></div>
            <div><div className="text-xs text-muted-foreground">Total Confirmed Received</div><div className="text-xl font-semibold text-green-600" data-testid="agg-received">{money(agg.total_confirmed_received)}</div></div>
            <div><div className="text-xs text-muted-foreground">Total Pending</div><div className="text-xl font-semibold text-orange-600" data-testid="agg-pending">{money(agg.total_pending)}</div></div>
          </div>
          {agg.total_awaiting_confirmation > 0 && (
            <p className="text-xs text-amber-600 mt-2">Awaiting confirmation (not counted): {money(agg.total_awaiting_confirmation)}</p>
          )}
        </div>
      )}

      {tab === 'overview' && canProfit && prof && (
        <>
          <p className="text-xs text-muted-foreground" data-testid="finance-kpi-scope">
            Profitability scope: WON projects only, within the selected period — so these figures can be
            lower than the Company Contract Ledger above, which also counts project-like leads still in play.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="finance-kpis">
            {[['Revenue', prof.total_revenue], ['Received', prof.total_received], ['Outstanding', prof.total_outstanding], ['Expenses', prof.total_expenses], ['Net Cash', prof.net_cash_position], ['Net Profit', prof.net_profit]].map(([l, v]) => (
              <div key={l} className="clay-card p-3"><div className="text-xs text-muted-foreground">{l}</div><div className={`text-lg font-semibold ${l === 'Net Profit' && v < 0 ? 'text-red-600' : ''}`}>{money(v)}</div></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="clay-card p-4">
              <h3 className="font-medium mb-3">Month-wise Revenue / Expenses / Net Profit</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={prof.month_wise}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="month" fontSize={10} /><YAxis fontSize={10} /><Tooltip formatter={(v) => money(v)} /><Legend />
                  <Bar dataKey="revenue" fill="#10B981" name="Revenue" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expenses" fill="#EF4444" name="Expenses" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="net_profit" fill="#3B82F6" name="Net Profit" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="clay-card p-4">
              <h3 className="font-medium mb-3">Category-wise Expenses</h3>
              {prof.category_wise.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No expenses in range.</p> : (
                <div className="space-y-2">
                  {prof.category_wise.map((c) => (
                    <div key={c.category} data-testid="finance-cat-row">
                      <div className="flex justify-between text-sm mb-1"><span>{c.category}</span><span className="font-medium">{money(c.amount)} · {c.pct}%</span></div>
                      <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${c.pct}%` }} /></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {tab === 'overview' && !canProfit && <div className="clay-card p-6 text-sm text-muted-foreground">You don't have permission to view profitability. Switch to the Expenses tab.</div>}

      {tab === 'expenses' && (
        <div className="space-y-4">
          {/* Category management */}
          {canAdd && (
            <div className="clay-card p-4">
              <h3 className="font-medium mb-2 text-sm">Expense Categories</h3>
              <div className="flex flex-wrap gap-2 items-center">
                {categories.map(c => (
                  <span key={c.id} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded-full text-xs">{c.name}
                    {!c.is_default && canEdit && <button onClick={async () => { await financeAPI.deleteCategory(c.id); load(); }} className="text-red-500"><Trash2 className="h-3 w-3" /></button>}
                  </span>
                ))}
                <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="New category" data-testid="finance-new-category" className="px-2 py-1 border rounded-lg text-xs bg-white dark:bg-slate-900" />
                <button onClick={addCategory} data-testid="finance-add-category" className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-lg">Add</button>
              </div>
            </div>
          )}
          <div className="clay-card p-4">
            <div className="flex justify-between mb-3"><h3 className="font-medium">Expenses</h3><span className="text-sm font-semibold">Total: {money(expenses.total)}</span></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground text-xs"><tr><th className="py-2">Date</th><th>Category</th><th>Description</th><th>Vendor</th><th>Method</th><th>Amount</th><th>By</th><th></th></tr></thead>
                <tbody>
                  {expenses.expenses.length === 0 ? <tr><td colSpan="8" className="py-6 text-center text-muted-foreground">No expenses recorded.</td></tr> :
                    expenses.expenses.map((ex) => (
                      <tr key={ex.id} className="border-t border-slate-100 dark:border-slate-800" data-testid="finance-expense-row">
                        <td className="py-2">{(ex.expense_date || '').slice(0, 10)}</td>
                        <td>{ex.category}</td><td className="max-w-[160px] truncate">{ex.description}</td><td>{ex.vendor}</td>
                        <td>{ex.payment_method}</td><td className="font-medium">{money(ex.amount)}</td><td>{ex.created_by_name}</td>
                        <td className="flex gap-1 py-2">
                          {canEdit && <button onClick={() => editExpense(ex)} data-testid="finance-edit-expense" className="text-primary"><Pencil className="h-4 w-4" /></button>}
                          {canDelete && <button onClick={() => delExpense(ex.id)} data-testid="finance-delete-expense" className="text-red-500"><Trash2 className="h-4 w-4" /></button>}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Expense form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowForm(false)}>
          <div className="clay-card p-6 w-full max-w-lg space-y-3" onClick={e => e.stopPropagation()} data-testid="finance-expense-form">
            <h3 className="text-lg font-medium">{editId ? 'Edit Expense' : 'Add Expense'}</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <label className="col-span-1">Date<input type="date" value={form.expense_date} onChange={e => setForm({ ...form, expense_date: e.target.value })} data-testid="expense-date" className="w-full mt-1 px-2 py-1.5 border rounded-lg bg-white dark:bg-slate-900" /></label>
              <label className="col-span-1">Category<select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="expense-category" className="w-full mt-1 px-2 py-1.5 border rounded-lg bg-white dark:bg-slate-900"><option value="">Select…</option>{categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}</select></label>
              <label className="col-span-1">Amount<input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" className="w-full mt-1 px-2 py-1.5 border rounded-lg bg-white dark:bg-slate-900" /></label>
              <label className="col-span-1">Payment Method<select value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded-lg bg-white dark:bg-slate-900"><option value="bank">Bank</option><option value="cash">Cash</option><option value="upi">UPI</option><option value="card">Card</option><option value="cheque">Cheque</option></select></label>
              <label className="col-span-1">Vendor/Payee<input value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded-lg bg-white dark:bg-slate-900" /></label>
              <label className="col-span-1">Reference<input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded-lg bg-white dark:bg-slate-900" /></label>
              <label className="col-span-2">Description<input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded-lg bg-white dark:bg-slate-900" /></label>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm">Cancel</button>
              <button onClick={saveExpense} data-testid="expense-save" className="px-4 py-2 text-sm bg-primary text-white rounded-lg">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default FinancePage;
